// src/pages/tournamentDetails/components/EarlyBirdCard.jsx
import React from "react";

function toLocalInputValue(value) {
  if (!value) return "";

  try {
    let d = null;

    if (typeof value?.toDate === "function") {
      d = value.toDate();
    } else if (value instanceof Date) {
      d = value;
    } else {
      d = new Date(value);
    }

    if (!d || Number.isNaN(d.getTime())) return "";

    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    const hours = String(d.getHours()).padStart(2, "0");
    const minutes = String(d.getMinutes()).padStart(2, "0");

    return `${year}-${month}-${day}T${hours}:${minutes}`;
  } catch {
    return "";
  }
}

function formatCutoffLabel(value) {
  if (!value) return "Not set";

  try {
    let d = null;

    if (typeof value?.toDate === "function") {
      d = value.toDate();
    } else if (value instanceof Date) {
      d = value;
    } else {
      d = new Date(value);
    }

    if (!d || Number.isNaN(d.getTime())) return "Not set";

    return d.toLocaleString([], {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  } catch {
    return "Not set";
  }
}

export default function EarlyBirdCard({
  isAdmin,
  isCompleted,
  isRegistering,
  isRunning,
  formatMoney,
  tournamentDate,
  now = Date.now(),

  // stored values
  earlyBirdEnabled,
  earlyBirdBonusChips,
  earlyBirdCutoffAt,

  // drafts
  earlyBirdEnabledDraft,
  setEarlyBirdEnabledDraft,
  earlyBirdBonusChipsDraft,
  setEarlyBirdBonusChipsDraft,
  earlyBirdCutoffAtDraft,
  setEarlyBirdCutoffAtDraft,

  // save state
  earlyBirdDirty,
  savingEarlyBirdSettings,
  onSaveEarlyBirdSettings,
}) {
  const locked = !isAdmin || isCompleted || !isRegistering;

  const YELLOW = {
    border: "rgba(234,179,8,0.35)",
    bg: "rgba(234,179,8,0.12)",
    text: "rgba(253,224,71,0.95)",
    glow: "rgba(234,179,8,0.18)",
  };

  const NEUTRAL_PILL = {
    borderColor: "rgba(113,113,122,0.35)",
    background: "rgba(113,113,122,0.12)",
    color: "rgba(228,228,231,0.95)",
  };

  const cutoffMillis =
    earlyBirdCutoffAt && typeof earlyBirdCutoffAt?.toMillis === "function"
      ? earlyBirdCutoffAt.toMillis()
      : earlyBirdCutoffAt
      ? new Date(earlyBirdCutoffAt).getTime()
      : null;

  const tournamentMillis =
    tournamentDate && typeof tournamentDate?.toMillis === "function"
      ? tournamentDate.toMillis()
      : tournamentDate
      ? new Date(tournamentDate).getTime()
      : null;

  const earlyBirdStatus = !earlyBirdEnabled
    ? "disabled"
    : cutoffMillis && Number.isFinite(cutoffMillis) && now <= cutoffMillis
    ? "active"
    : "expired";

  const bonusMissing =
    !!earlyBirdEnabledDraft && !(Number(earlyBirdBonusChipsDraft || 0) > 0);

  const cutoffMissing = !!earlyBirdEnabledDraft && !String(earlyBirdCutoffAtDraft || "").trim();

  let cutoffAfterTournament = false;
  if (earlyBirdEnabledDraft && earlyBirdCutoffAtDraft && tournamentMillis) {
    const draftMillis = new Date(earlyBirdCutoffAtDraft).getTime();
    cutoffAfterTournament =
      Number.isFinite(draftMillis) &&
      Number.isFinite(tournamentMillis) &&
      draftMillis >= tournamentMillis;
  }

  const saveDisabled =
    locked ||
    savingEarlyBirdSettings ||
    !earlyBirdDirty ||
    bonusMissing ||
    cutoffMissing ||
    cutoffAfterTournament;

  const Toggle = ({ on, disabled }) => (
    <span
      className={`relative inline-flex h-9 w-16 items-center rounded-full border transition ${
        on ? "bg-zinc-800" : "bg-zinc-900"
      } ${disabled ? "opacity-60 cursor-not-allowed" : "hover:border-zinc-600"}`}
      style={{
        borderColor: on ? "rgba(255,255,255,0.22)" : "rgba(255,255,255,0.12)",
      }}
    >
      <span
        className={`inline-block h-7 w-7 transform rounded-full bg-zinc-100 transition ${
          on ? "translate-x-8" : "translate-x-1"
        }`}
      />
    </span>
  );

  return (
    <div className="bg-zinc-900 p-4 rounded-lg border border-zinc-800 mb-8">
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <h3 className="text-xl font-semibold text-[#B87333]">Early Bird</h3>

          <div className="mt-2 flex flex-wrap items-center gap-2">
            <span
              className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold border"
              style={
                earlyBirdEnabled
                  ? {
                      borderColor: YELLOW.border,
                      background: YELLOW.bg,
                      color: YELLOW.text,
                    }
                  : NEUTRAL_PILL
              }
              title={earlyBirdEnabled ? "Early Bird enabled" : "Early Bird disabled"}
            >
              {earlyBirdEnabled ? "★ Early Bird On" : "Early Bird Off"}
            </span>

            {earlyBirdEnabled ? (
              <span className="text-zinc-400 text-sm">
                Bonus Chips:{" "}
                <span className="text-zinc-200 font-semibold">
                  +{Number(earlyBirdBonusChips || 0).toLocaleString()}
                </span>
              </span>
            ) : null}
          </div>

          <div className="mt-2 text-zinc-500 text-xs space-y-1">
            <div>
              Status:{" "}
              {earlyBirdStatus === "active" ? (
                <span className="text-green-400 font-semibold">ACTIVE</span>
              ) : earlyBirdStatus === "expired" ? (
                <span className="text-yellow-300 font-semibold">EXPIRED</span>
              ) : (
                <span className="text-zinc-400 font-semibold">DISABLED</span>
              )}
            </div>

            <div>
              Cutoff:{" "}
              <span className="text-zinc-200 font-semibold">
                {formatCutoffLabel(earlyBirdCutoffAt)}
              </span>
            </div>

            {earlyBirdEnabled ? (
              <div>
                Entrants who register before the cutoff receive{" "}
                <span className="text-zinc-200 font-semibold">
                  +{Number(earlyBirdBonusChips || 0).toLocaleString()} chips
                </span>
                .
              </div>
            ) : null}

            {isRunning ? (
              <div>
                Tournament is running — Early Bird is now{" "}
                <span className="text-zinc-200 font-semibold">view-only</span>.
              </div>
            ) : null}
          </div>
        </div>

        {isAdmin ? (
          <button
            type="button"
            onClick={onSaveEarlyBirdSettings}
            disabled={saveDisabled}
            className={`px-4 py-2 rounded font-semibold ${
              saveDisabled
                ? "bg-zinc-700 text-zinc-300 cursor-not-allowed"
                : "bg-green-700 hover:bg-green-800 text-white"
            }`}
            title={
              locked
                ? "Editable while registering"
                : bonusMissing
                ? "Early Bird requires bonus chips greater than 0"
                : cutoffMissing
                ? "Early Bird requires a cutoff date and time"
                : cutoffAfterTournament
                ? "Cutoff must be before the tournament start date/time"
                : !earlyBirdDirty
                ? "No changes to save"
                : "Save Early Bird settings"
            }
          >
            {savingEarlyBirdSettings ? "Saving..." : "Save Early Bird"}
          </button>
        ) : null}
      </div>

      {isAdmin ? (
        <div className="border-t border-zinc-800 pt-4 mt-4">
          <div className="grid gap-4 md:grid-cols-3 items-start">
            <div
              className="rounded-lg p-3 border"
              style={{
                borderColor: earlyBirdEnabledDraft ? YELLOW.border : "rgba(255,255,255,0.10)",
                background: earlyBirdEnabledDraft ? "rgba(234,179,8,0.06)" : "rgba(0,0,0,0.20)",
                boxShadow: earlyBirdEnabledDraft ? `0 0 18px ${YELLOW.glow}` : "none",
              }}
            >
              <div className="flex items-center justify-between gap-3">
                <div>
                  <p className="text-zinc-200 text-sm font-semibold">Enable Early Bird</p>
                  <p className="text-zinc-500 text-xs">Bonus chips for early registration</p>
                </div>

                <button
                  type="button"
                  onClick={() => {
                    const next = !earlyBirdEnabledDraft;
                    setEarlyBirdEnabledDraft(next);
                    if (!next) {
                      setEarlyBirdBonusChipsDraft("0");
                      setEarlyBirdCutoffAtDraft("");
                    }
                  }}
                  disabled={locked}
                  className="select-none"
                  title={locked ? "Editable while registering" : "Toggle Early Bird"}
                >
                  <Toggle on={earlyBirdEnabledDraft} disabled={locked} />
                </button>
              </div>

              {earlyBirdEnabledDraft ? (
                <div className="mt-3 grid gap-2">
                  <div>
                    <label className="block text-zinc-300 text-xs mb-1">Bonus Chips</label>
                    <input
                      type="number"
                      className="w-full p-2 bg-zinc-900 text-zinc-100 rounded border"
                      style={{
                        borderColor: bonusMissing ? YELLOW.border : "rgba(255,255,255,0.10)",
                        boxShadow: bonusMissing ? "0 0 0 3px rgba(234,179,8,0.10)" : "none",
                      }}
                      value={earlyBirdBonusChipsDraft}
                      onChange={(e) => setEarlyBirdBonusChipsDraft(e.target.value)}
                      min="0"
                      step="1"
                      disabled={locked}
                    />
                    {bonusMissing ? (
                      <p className="text-yellow-300 text-xs mt-1">
                        Enter bonus chips greater than 0.
                      </p>
                    ) : null}
                  </div>

                  <div>
                    <label className="block text-zinc-300 text-xs mb-1">Cutoff Date & Time</label>
                    <input
                      type="datetime-local"
                      className="w-full p-2 bg-zinc-900 text-zinc-100 rounded border"
                      style={{
                        borderColor:
                          cutoffMissing || cutoffAfterTournament
                            ? YELLOW.border
                            : "rgba(255,255,255,0.10)",
                        boxShadow:
                          cutoffMissing || cutoffAfterTournament
                            ? "0 0 0 3px rgba(234,179,8,0.10)"
                            : "none",
                      }}
                      value={toLocalInputValue(earlyBirdCutoffAtDraft)}
                      onChange={(e) => setEarlyBirdCutoffAtDraft(e.target.value)}
                      disabled={locked}
                    />

                    {cutoffMissing ? (
                      <p className="text-yellow-300 text-xs mt-1">
                        Select the Early Bird cutoff date and time.
                      </p>
                    ) : cutoffAfterTournament ? (
                      <p className="text-yellow-300 text-xs mt-1">
                        Cutoff must be before the tournament start date/time.
                      </p>
                    ) : (
                      <p className="text-zinc-500 text-xs mt-1">
                        Tournament start:{" "}
                        <span className="text-zinc-300 font-semibold">
                          {formatCutoffLabel(tournamentDate)}
                        </span>
                      </p>
                    )}
                  </div>
                </div>
              ) : null}
            </div>

            <div className="rounded-lg p-3 border border-zinc-800 bg-black/20">
              <p className="text-zinc-200 text-sm font-semibold">Current Preview</p>

              <div className="mt-3 space-y-2 text-sm">
                <div className="flex items-center justify-between gap-3">
                  <span className="text-zinc-500">Enabled</span>
                  <span className="text-zinc-100 font-semibold">
                    {earlyBirdEnabledDraft ? "Yes" : "No"}
                  </span>
                </div>

                <div className="flex items-center justify-between gap-3">
                  <span className="text-zinc-500">Bonus</span>
                  <span className="text-zinc-100 font-semibold">
                    +{Number(earlyBirdBonusChipsDraft || 0).toLocaleString()} chips
                  </span>
                </div>

                <div className="flex items-start justify-between gap-3">
                  <span className="text-zinc-500">Cutoff</span>
                  <span className="text-zinc-100 font-semibold text-right">
                    {earlyBirdCutoffAtDraft
                      ? formatCutoffLabel(new Date(earlyBirdCutoffAtDraft))
                      : "Not set"}
                  </span>
                </div>
              </div>
            </div>

            <div className="text-zinc-500 text-xs leading-relaxed">
              <div className="font-semibold text-zinc-300 mb-1">Editing Rules</div>
              <ul className="list-disc pl-5 space-y-1">
                <li>
                  Early Bird can only be changed while{" "}
                  <span className="text-zinc-200 font-semibold">Registering</span>.
                </li>
                <li>Bonus chips must be greater than 0 when enabled.</li>
                <li>Cutoff must be before the tournament start date/time.</li>
                <li>
                  On the entrants list, qualified players can show a yellow{" "}
                  <span className="text-zinc-200 font-semibold">Early Bird</span> pill.
                </li>
              </ul>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}